import React, { useState, useEffect } from 'react';

const GameTransactionHistory = ({
  gameId,
  gameMakerContract,
  gameContract,
  gameEvents, // Multi-event support: [{ name: 'ChoiceMade', label: 'Choice Made' }, { name: 'ChoiceRevealed', label: 'Choice Revealed' }]
  player1,
  player2
}) => {
  const [transactions, setTransactions] = useState({
    offerTx: 'Loading...',
    acceptTx: 'Loading...',
    moveTxs: [],
    killTx: 'Loading...',
    payoutTx: 'Loading...'
  });
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isCollapsed, setIsCollapsed] = useState(true);

  const loadTransactionHistory = async () => {
    if (!gameId || !gameMakerContract || !gameContract) {
      console.log('[GameTransactionHistory] Missing required props');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      console.log(`[GameTransactionHistory] Loading transaction history for game ${gameId}`);

      // Optimized 4-call pattern:
      // 1. Get accept event for THIS specific game (1 result)
      const acceptEvents = await gameMakerContract.queryFilter(
        gameMakerContract.filters.GameAccepted(gameId),
        0,
        'latest'
      );
      console.log(`[GameTransactionHistory] Found ${acceptEvents.length} accept events`);

      let offerEvents = [];
      if (acceptEvents.length > 0) {
        // 2. Extract offerId and get the specific offer (1 result)
        const offerId = acceptEvents[0].args.offerId;
        console.log(`[GameTransactionHistory] Found offerId: ${offerId}`);

        offerEvents = await gameMakerContract.queryFilter(
          gameMakerContract.filters.GameOffered(offerId),
          0,
          'latest'
        );
        console.log(`[GameTransactionHistory] Found ${offerEvents.length} offer events`);
      }

      // 3. Get game completion events
      const finishedEvents = await gameContract.queryFilter(
        gameContract.filters.GameFinished(gameId),
        0,
        'latest'
      );
      console.log(`[GameTransactionHistory] Found ${finishedEvents.length} finished events`);

      // 4. Get game events
      let allGameEvents = [];

      for (const eventConfig of gameEvents) {
        try {
          const events = await gameContract.queryFilter(
            gameContract.filters[eventConfig.name](gameId),
            0,
            'latest'
          );
          console.log(`[GameTransactionHistory] Found ${events.length} ${eventConfig.name} events`);

          // Add event type info to each event
          events.forEach(event => {
            event.eventType = eventConfig.name;
            event.eventLabel = eventConfig.label || eventConfig.name;
          });

          allGameEvents.push(...events);
        } catch (error) {
          console.warn(`[GameTransactionHistory] Failed to query ${eventConfig.name} events:`, error);
        }
      }

      // 5. Get kill events for this game
      const killEvents = await gameContract.queryFilter(
        gameContract.filters.GameKilled(gameId),
        0,
        'latest'
      );
      console.log(`[GameTransactionHistory] Found ${killEvents.length} kill events`);

      // Sort all game events by block number and transaction index for chronological order
      const sortedGameEvents = allGameEvents.sort((a, b) => {
        const blockDiff = Number(a.blockNumber) - Number(b.blockNumber);
        if (blockDiff !== 0) return blockDiff;
        // If same block, sort by transaction index
        return Number(a.transactionIndex || 0) - Number(b.transactionIndex || 0);
      });

      // Process game events with their types and labels
      const processedMoves = sortedGameEvents.map((event, index) => ({
        txHash: event.transactionHash,
        player: event.args.player || 'System',
        eventType: event.eventType,
        eventLabel: event.eventLabel,
        blockNumber: event.blockNumber,
        transactionIndex: event.transactionIndex || 0,
        moveNumber: index + 1,
        // Include specific event args based on type
        ...(event.args.index !== undefined && { index: event.args.index }),
        ...(event.args.turnNumber !== undefined && { turnNumber: event.args.turnNumber }),
        ...(event.args.choice !== undefined && { choice: event.args.choice }),
        ...(event.args.nonce !== undefined && { nonce: event.args.nonce })
      }));

      // Determine payout transaction
      let payoutTx = 'Pending';
      if (killEvents.length > 0) {
        payoutTx = killEvents[0].transactionHash;
      } else if (finishedEvents.length > 0) {
        payoutTx = finishedEvents[0].transactionHash;
      }

      setTransactions({
        offerTx: offerEvents.length > 0 ? offerEvents[0].transactionHash : 'Not found',
        acceptTx: acceptEvents.length > 0 ? acceptEvents[0].transactionHash : 'Not found',
        moveTxs: processedMoves,
        killTx: killEvents.length > 0 ? killEvents[0].transactionHash : 'Not found',
        payoutTx: payoutTx
      });


      console.log(`[GameTransactionHistory] Transaction history loaded successfully for game ${gameId}`);
    } catch (error) {
      console.error(`[GameTransactionHistory] Failed to load transaction history for game ${gameId}:`, error);
      setError(error.message);
      setTransactions({
        offerTx: 'Error loading',
        acceptTx: 'Error loading',
        moveTxs: [],
        killTx: 'Error loading',
        payoutTx: 'Error loading'
      });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadTransactionHistory();
  }, [gameId, gameMakerContract, gameContract, gameEvents]);

  // Remove automatic polling updates to prevent flickering
  // Transaction history will be loaded once on mount and can be manually refreshed
  // This prevents the constant opening/closing issue caused by frequent polling updates

  const formatAddress = (address) => {
    if (!address) return 'Unknown';
    return `${address.slice(0, 6)}...${address.slice(-4)}`;
  };

  const formatTxHash = (hash) => {
    if (!hash || hash === 'Loading...' || hash === 'Error loading' || hash === 'Not found' || hash === 'Pending') {
      return hash;
    }
    return `${hash.slice(0, 10)}...${hash.slice(-8)}`;
  };

  return (
    <div className="border-t pt-4">
      <button
        className="collapsible-button"
        onClick={() => setIsCollapsed(!isCollapsed)}
        style={{
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          fontSize: '1.125rem',
          fontWeight: '600',
          cursor: 'pointer',
          marginBottom: '0.75rem'
        }}
      >
        <span>Transaction History</span>
        <svg className={`w-5 h-5 transition-transform duration-200 ${isCollapsed ? '' : 'rotate-180'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      <div className={`transition-all duration-300 ease-in-out ${
        isCollapsed ? 'max-h-0 opacity-0 overflow-hidden' : 'opacity-100'
      }`}>
        {isLoading ? (
          <div className="p-4 bg-gray-100 rounded">
            <div>Loading transaction history...</div>
          </div>
        ) : error ? (
          <div className="p-4 bg-red-100 rounded">
            <div className="text-red-600">Error: {error}</div>
            <button
              onClick={loadTransactionHistory}
              className="mt-2 px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
            >
              Retry
            </button>
          </div>
        ) : (
          <div className="space-y-3">
            <div>
              <p className="text-sm text-gray-600 mb-1">Offer Transaction</p>
              <div className="bg-gray-50 p-2 rounded">
                {formatTxHash(transactions.offerTx)}
              </div>
            </div>

            <div>
              <p className="text-sm text-gray-600 mb-1">Accept Transaction</p>
              <div className="bg-gray-50 p-2 rounded">
                {formatTxHash(transactions.acceptTx)}
              </div>
            </div>

            {transactions.moveTxs.length > 0 && (
              <div>
                <p className="text-sm text-gray-600 mb-2">Game Events (Chronological Order)</p>
                <div className="space-y-2">
                  {transactions.moveTxs.map((move, index) => (
                    <div key={index} className="bg-blue-50 p-2 rounded">
                      <div className="flex justify-between items-center mb-1">
                        <span className="text-sm font-medium text-gray-700">
                          {move.eventLabel} by {formatAddress(move.player)}
                          {move.index !== undefined && ` (Position: ${move.index})`}
                          {move.turnNumber !== undefined && ` (Turn: ${move.turnNumber})`}
                          {move.choice !== undefined && ` (Choice: ${move.choice})`}
                        </span>
                        <span className="text-xs text-gray-500">
                          #{move.moveNumber}
                        </span>
                      </div>
                      <div className="text-gray-600 text-sm">
                        {formatTxHash(move.txHash)}
                      </div>
                      <div className="text-xs text-gray-400">
                        Block: {move.blockNumber} • Type: {move.eventType}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {transactions.killTx !== 'Not found' && (
              <div>
                <p className="text-sm text-gray-600 mb-2">Kill & Payout Transaction</p>
                <div className="bg-red-50 p-2 rounded">
                  <div className="flex justify-between items-center mb-1">
                    <span className="text-sm font-medium text-gray-700">
                      Game Killed (Timeout) + Refunds Processed
                    </span>
                  </div>
                  <div className="text-gray-600">
                    {formatTxHash(transactions.killTx)}
                  </div>
                </div>
              </div>
            )}

            <div>
              <p className="text-sm text-gray-600 mb-1">Payout Transaction</p>
              <div className="bg-gray-50 p-2 rounded">
                {formatTxHash(transactions.payoutTx)}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default GameTransactionHistory;